import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../Context/AuthContext";
import { useTranslation } from "react-i18next";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import LanguageIcon from "@mui/icons-material/Language";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import HomeIcon from "@mui/icons-material/Home";
import StorefrontIcon from "@mui/icons-material/Storefront";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import FiberNewIcon from "@mui/icons-material/FiberNew";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { useState, useRef, useMemo, useEffect } from "react";
import { useProducts } from "../../Context/ProductsContext";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import { getCountriesWithShippingCategories } from "../../services/Public/PublicPagesApi";
import CartDrawer from "../../components/CartDrawer";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import Collapse from "@mui/material/Collapse";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import logo from "../../assets/didalogo.png";
import { resolveImageUrl } from "../../services/indexMediaBaseUrl";
import Badge from "@mui/material/Badge";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";

const Navbar = () => {
  const { isAuth, logout, userType } = useAuth();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation("navbar");
  const [search, setSearch] = useState("");
  const { flatProducts } = useProducts();
  const inputRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const [cartOpen, setCartOpen] = useState(false);
  const [cartItems, setCartItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem("cart") || "[]"); } catch { return []; }
  });

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "cart") {
        try { setCartItems(JSON.parse(e.newValue || "[]")); } catch { setCartItems([]); }
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const refreshCartFromStorage = () => {
    try { setCartItems(JSON.parse(localStorage.getItem("cart") || "[]")); } catch { setCartItems([]); }
  };

  // Search handlers
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    if (!val.trim()) {
      setSuggestions([]);
      setAnchorEl(null);
      return;
    }
    const q = val.trim().toLowerCase();
    const matched = (flatProducts || []).filter((p) => {
      const nameAr = String(p.name_ar || "").toLowerCase();
      const nameEn = String(p.name_en || "").toLowerCase();
      return nameAr.includes(q) || nameEn.includes(q);
    }).slice(0, 8);
    setSuggestions(matched);
    setAnchorEl(matched.length ? inputRef.current : null);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (!search.trim()) return;
    navigate(`/shop?search=${encodeURIComponent(search.trim())}`);
    setSuggestions([]);
    setAnchorEl(null);
    setSearchExpanded(false);
  };

  const handleSuggestionClick = (product) => {
    navigate(`/product/${product.id}`);
    setSearch("");
    setSuggestions([]);
    setAnchorEl(null);
    setSearchExpanded(false);
  };

  const handleCloseSuggestions = () => {
    setSuggestions([]);
    setAnchorEl(null);
  };

  const openCart = () => { refreshCartFromStorage(); setCartOpen(true); };
  const closeCart = () => setCartOpen(false);

  const removeCartItem = (id) => {
    const next = cartItems.filter((it) => String(it.id) !== String(id));
    setCartItems(next);
    try { localStorage.setItem("cart", JSON.stringify(next)); } catch {}
  };

  const handleLogout = () => { logout(); navigate("/login"); };
  const toggleLanguage = () => {
    const newLang = i18n.language === "ar" ? "en" : "ar";
    i18n.changeLanguage(newLang);
    localStorage.setItem("lang", newLang);
    document.documentElement.dir = newLang === "ar" ? "rtl" : "ltr";
  };

  const localeName = (p) => {
    let lang = (i18n && i18n.language) || localStorage.getItem("lang") || "ar";
    if (lang === "rn") lang = "en";
    lang = lang.startsWith("ar") ? "ar" : "en";
    return lang === "ar" ? (p.name_ar || p.name_en) : (p.name_en || p.name_ar);
  };

  const STORAGE_KEY = "selectedCountryCode";
  const [selectedCountryCode, setSelectedCountryCode] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || ""; } catch { return ""; }
  });

  const [countries, setCountries] = useState([]);
  useEffect(() => {
    let mounted = true;
    const fetchCountries = async () => {
      try {
        const res = await getCountriesWithShippingCategories();
        const list = res?.countries || res?.data?.countries || res?.data || [];
        if (mounted) {
          setCountries(Array.isArray(list) ? list : []);
          try {
            const storedCountryId = localStorage.getItem("country_id");
            if (storedCountryId) {
              const found = (Array.isArray(list) ? list : []).find(
                (c) => String(c.id) === String(storedCountryId) || String(c.code) === String(storedCountryId)
              );
              if (found && !localStorage.getItem(STORAGE_KEY)) {
                setSelectedCountryCode(found.code || String(found.id));
              }
            }
          } catch {}
        }
      } catch (err) {
        console.error("Navbar:getCountriesWithShippingCategories error", err);
      }
    };
    fetchCountries();
    return () => { mounted = false; };
  }, []);

  const selectedCountry = useMemo(
    () => (countries || []).find((c) => String(c.id) === String(selectedCountryCode) || c.code === selectedCountryCode) || null,
    [selectedCountryCode, countries]
  );

  const displayedCountryName = useMemo(() => {
    if (!selectedCountry) return "";
    const lang = (i18n?.language || "en").startsWith("ar") ? "ar" : "en";
    return lang === "ar" ? selectedCountry.name_ar : selectedCountry.name_en;
  }, [selectedCountry, i18n?.language]);

  const handleCountryChange = (e) => {
    const code = e.target.value || "";
    const prev = selectedCountryCode;
    setSelectedCountryCode(code);
    try {
      if (code) localStorage.setItem(STORAGE_KEY, code);
      else localStorage.removeItem(STORAGE_KEY);
    } catch {}
    try {
      if (!code) {
        localStorage.removeItem("country_id");
        if (prev && prev !== "") window.location.reload();
        return;
      }
      const country = (countries || []).find(
        (c) => String(c.code) === String(code) || String(c.id) === String(code)
      );
      if (country && country.id !== undefined && country.id !== null) {
        localStorage.setItem("country_id", String(country.id));
      } else {
        if (!isNaN(Number(code))) localStorage.setItem("country_id", String(code));
        else localStorage.removeItem("country_id");
      }
    } catch {}
    if (String(code) !== String(prev)) window.location.reload();
  };

  const navLinks = [
    { to: "/shop", label: t("products"), icon: <StorefrontIcon />, show: true },
    { to: "/saved-products", label: t("saved_products"), icon: <BookmarkBorderIcon />, show: isAuth },
    { to: "/myorders", label: t("my_orders"), icon: <ShoppingBagIcon />, show: isAuth },
    { to: "/best-sellers", label: t("best_sellers"), icon: <WhatshotIcon />, show: true, color: "error.main" },
    { to: "/new-arrivals", label: t("new_arrivals"), icon: <FiberNewIcon />, show: true },
    { to: "/top-rated", label: t("top_rated"), icon: <StarBorderIcon />, show: true },
  ];

  const handleMobileNav = (to) => {
    navigate(to);
    setMobileOpen(false);
  };

  const SuggestionsDropdown = () => (
    anchorEl && suggestions && suggestions.length > 0 ? (
      <Box
        sx={{
          position: "absolute",
          top: "100%",
          left: 0,
          right: 0,
          bgcolor: "background.paper",
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          borderRadius: 3,
          zIndex: 1300,
          maxHeight: 420,
          overflow: "auto",
          mt: 0.5,
          border: "1px solid",
          borderColor: "grey.100",
        }}
        onMouseLeave={handleCloseSuggestions}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "grey.100" }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            {suggestions.length} {(i18n?.language || "ar").startsWith("ar") ? "نتيجة" : "results"}
          </Typography>
        </Box>
        {suggestions.map((p) => {
          const img = p.main_photo || p.images?.[0]?.image || "";
          const imgSrc = img ? resolveImageUrl(img) : "/placeholder.png";
          const pName = localeName(p);
          const catName = p.categroy
            ? ((i18n?.language || "ar").startsWith("ar")
              ? (p.categroy.name_ar || p.categroy.name_en)
              : (p.categroy.name_en || p.categroy.name_ar))
            : "";
          const currencies = p.currencies || p.Currencies || [];
          const countryId = (() => { try { return localStorage.getItem("country_id"); } catch { return null; } })();
          let priceEntry = countryId ? currencies.find(c => String(c.country_id) === String(countryId)) : null;
          if (!priceEntry && currencies.length) priceEntry = currencies[0];
          const price = priceEntry?.price_after_discount || priceEntry?.price || p.price_after_discount || p.price || null;

          return (
            <Box
              key={p.id}
              sx={{
                px: 2,
                py: 1.5,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 2,
                transition: "all 0.2s ease",
                "&:hover": { bgcolor: "primary.50" },
                borderBottom: "1px solid",
                borderColor: "grey.50",
                "&:last-child": { borderBottom: "none" },
              }}
              onClick={() => handleSuggestionClick(p)}
            >
              {/* Product Image */}
              <Box
                component="img"
                src={imgSrc}
                alt={pName}
                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/placeholder.png"; }}
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  objectFit: "cover",
                  border: "1px solid",
                  borderColor: "grey.200",
                  flexShrink: 0,
                }}
              />

              {/* Product Info */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    color: "text.primary",
                  }}
                >
                  {pName}
                </Typography>
                {catName && (
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: "block", mt: 0.3 }}>
                    {catName}
                  </Typography>
                )}
              </Box>

              {/* Price */}
              {price && (
                <Typography
                  variant="subtitle2"
                  fontWeight={800}
                  color="primary.main"
                  sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                >
                  {price} {(i18n?.language || "ar").startsWith("ar") ? "ج.م" : "EGP"}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    ) : null
  );

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: "rgba(255,255,255,0.97)",
          borderBottom: "1px solid",
          borderColor: "grey.100",
          backdropFilter: "blur(12px)",
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "2px",
            background: "linear-gradient(90deg, #d5aa2f 0%, #e6c76a 50%, #d5aa2f 100%)",
            opacity: 0.4,
          },
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            py: 0.5,
            minHeight: { xs: 56, md: 64 },
            px: { xs: 1, sm: 2, md: 3 },
          }}
        >
          {/* Left: Logo + Desktop Links */}
          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, md: 1 } }}>
            {/* Mobile hamburger */}
            {isMobile && (
              <IconButton
                onClick={() => setMobileOpen(true)}
                sx={{
                  color: "text.primary",
                  mr: 0.5,
                }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {/* Logo */}
            <Box
              component={Link}
              to="/"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                textDecoration: "none",
                color: "inherit",
                mr: { xs: 0, md: 1 },
              }}
            >
              <Box
                component="img"
                src={logo}
                alt="Dida Logo"
                sx={{
                  height: { xs: 36, md: 44 },
                  display: "block",
                  transition: "transform 0.2s",
                  "&:hover": { transform: "scale(1.05)" },
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  background: "linear-gradient(135deg, #d5aa2f 0%, #b08c26 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  display: { xs: "none", lg: "block" },
                  fontSize: "1.1rem",
                }}
              >
                {t("home")}
              </Typography>
            </Box>

            {/* Desktop nav links */}
            {!isMobile && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {navLinks.filter((l) => l.show).map((link) => (
                  <Button
                    key={link.to}
                    component={Link}
                    to={link.to}
                    variant="text"
                    size="small"
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      borderRadius: 2,
                      px: { md: 1.2, lg: 1.5 },
                      fontSize: { md: "0.8rem", lg: "0.875rem" },
                      color: link.color || "text.primary",
                      whiteSpace: "nowrap",
                      "&:hover": {
                        bgcolor: link.color ? "error.50" : "primary.50",
                        color: link.color || "primary.dark",
                      },
                    }}
                  >
                    {link.label}
                  </Button>
                ))}
              </Box>
            )}

            {/* Desktop search */}
            {!isMobile && (
              <Box
                component="form"
                onSubmit={handleSearchSubmit}
                sx={{ ml: 1, position: "relative" }}
              >
                <TextField
                  size="small"
                  inputRef={inputRef}
                  value={search}
                  onChange={handleSearchChange}
                  placeholder={t("search_placeholder")}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton type="submit" size="small" sx={{ color: "primary.main" }}>
                          <SearchIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: {
                      width: { md: 200, lg: 280 },
                      borderRadius: 3,
                      bgcolor: "grey.50",
                      "&:hover": { bgcolor: "grey.100" },
                      "& fieldset": { border: "none" },
                    },
                  }}
                />
                <SuggestionsDropdown />
              </Box>
            )}
          </Box>

          {/* Right: Actions */}
          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1 } }}>
            {/* Mobile search toggle */}
            {isMobile && (
              <IconButton
                onClick={() => setSearchExpanded(!searchExpanded)}
                sx={{
                  color: searchExpanded ? "primary.main" : "text.secondary",
                  bgcolor: searchExpanded ? "primary.50" : "transparent",
                }}
              >
                {searchExpanded ? <CloseIcon /> : <SearchIcon />}
              </IconButton>
            )}

            {/* Cart */}
            <CartDrawer />

            {/* Country selector */}
            <FormControl
              size="small"
              sx={{
                minWidth: { xs: 0, sm: 120, md: 160 },
                display: { xs: "none", sm: "flex" },
              }}
            >
              <Select
                value={selectedCountryCode}
                onChange={handleCountryChange}
                displayEmpty
                sx={{
                  borderRadius: 2,
                  bgcolor: "grey.50",
                  "& fieldset": { borderColor: "grey.200" },
                  fontSize: "0.8rem",
                }}
                renderValue={(val) => {
                  if (!val) return <Typography variant="caption" color="text.secondary">{t("choose_country")}</Typography>;
                  return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      {selectedCountry?.image ? (
                        <Box
                          component="img"
                          src={resolveImageUrl(selectedCountry.image)}
                          alt=""
                          sx={{ width: 20, height: 14, objectFit: "cover", borderRadius: 0.5 }}
                        />
                      ) : (
                        <span>{selectedCountry?.flag || ""}</span>
                      )}
                      <Typography variant="caption" fontWeight={600}>{displayedCountryName}</Typography>
                    </Box>
                  );
                }}
              >
                <MenuItem value="">
                  <em>{t("choose_country")}</em>
                </MenuItem>
                {(countries || []).map((c) => (
                  <MenuItem key={c.id} value={c.code || c.id}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {c.image ? (
                        <Box
                          component="img"
                          src={resolveImageUrl(c.image)}
                          alt=""
                          sx={{ width: 22, height: 16, objectFit: "cover", borderRadius: 0.5 }}
                        />
                      ) : (
                        <span>{c.flag || ""}</span>
                      )}
                      <span>{(i18n?.language || "en").startsWith("ar") ? c.name_ar : c.name_en}</span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Language */}
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <IconButton
                onClick={toggleLanguage}
                sx={{
                  bgcolor: "grey.50",
                  borderRadius: 2,
                  width: { xs: 36, md: 40 },
                  height: { xs: 36, md: 40 },
                  "&:hover": { bgcolor: "primary.50" },
                }}
              >
                <LanguageIcon fontSize="small" />
              </IconButton>
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.6rem",
                  fontWeight: 700,
                  color: "text.secondary",
                  mt: 0.2,
                  lineHeight: 1,
                  cursor: "pointer",
                  "&:hover": { color: "primary.main" },
                }}
                onClick={toggleLanguage}
              >
                {i18n.language === "ar" ? "EN" : "ع"}
              </Typography>
            </Box>

            {/* Auth */}
            {!isAuth ? (
              <Button
                component={Link}
                to="/login"
                variant="contained"
                size="small"
                sx={{
                  borderRadius: 3,
                  px: { xs: 2, md: 3 },
                  fontWeight: 700,
                  textTransform: "none",
                  fontSize: { xs: "0.75rem", md: "0.85rem" },
                  background: "linear-gradient(135deg, #d5aa2f 0%, #e6c76a 100%)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #b08c26 0%, #d5aa2f 100%)",
                  },
                }}
              >
                {t("login")}
              </Button>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {userType === "admin" && !isMobile && (
                  <Button
                    component={Link}
                    to="/admin/dashboard"
                    variant="text"
                    size="small"
                    startIcon={<DashboardIcon />}
                    sx={{ textTransform: "none", fontWeight: 700, fontSize: "0.8rem" }}
                  >
                    {t("dashboard")}
                  </Button>
                )}
                <IconButton
                  onClick={handleLogout}
                  sx={{
                    color: "text.secondary",
                    "&:hover": { color: "error.main", bgcolor: "error.50" },
                  }}
                >
                  <ExitToAppIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
        </Toolbar>

        {/* Mobile Search Bar (expandable) */}
        <Collapse in={isMobile && searchExpanded}>
          <Box
            component="form"
            onSubmit={handleSearchSubmit}
            sx={{
              px: 2,
              pb: 2,
              position: "relative",
            }}
          >
            <TextField
              size="small"
              fullWidth
              inputRef={inputRef}
              value={search}
              onChange={handleSearchChange}
              placeholder={t("search_placeholder")}
              autoFocus={searchExpanded}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton type="submit" size="small" sx={{ color: "primary.main" }}>
                      <SearchIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 3,
                  bgcolor: "grey.50",
                  "& fieldset": { border: "none" },
                },
              }}
            />
            <SuggestionsDropdown />
          </Box>
        </Collapse>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "85vw", sm: 320 },
            borderRadius: "16px 0 0 16px",
            bgcolor: "background.paper",
          },
        }}
      >
        {/* Drawer Header */}
        <Box
          sx={{
            p: 3,
            background: "linear-gradient(135deg, #d5aa2f 0%, #e6c76a 100%)",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              component="img"
              src={logo}
              alt="Dida"
              sx={{ height: 36, filter: "brightness(10)" }}
            />
            <Typography variant="h6" fontWeight={800}>
              Dida Store
            </Typography>
          </Box>
          <IconButton
            onClick={() => setMobileOpen(false)}
            sx={{ color: "white", "&:hover": { bgcolor: "rgba(255,255,255,0.2)" } }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Drawer Nav Links */}
        <List sx={{ px: 1.5, py: 2 }}>
          <ListItemButton
            onClick={() => handleMobileNav("/")}
            sx={{ borderRadius: 2, mb: 0.5 }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}><HomeIcon sx={{ color: "primary.main" }} /></ListItemIcon>
            <ListItemText primary={t("home")} primaryTypographyProps={{ fontWeight: 700 }} />
          </ListItemButton>

          {navLinks.filter((l) => l.show).map((link) => (
            <ListItemButton
              key={link.to}
              onClick={() => handleMobileNav(link.to)}
              sx={{ borderRadius: 2, mb: 0.5 }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: link.color || "primary.main" }}>
                {link.icon}
              </ListItemIcon>
              <ListItemText
                primary={link.label}
                primaryTypographyProps={{ fontWeight: 700, color: link.color || "text.primary" }}
              />
            </ListItemButton>
          ))}

          {isAuth && userType === "admin" && (
            <ListItemButton
              onClick={() => handleMobileNav("/admin/dashboard")}
              sx={{ borderRadius: 2, mb: 0.5 }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}><DashboardIcon sx={{ color: "primary.main" }} /></ListItemIcon>
              <ListItemText primary={t("dashboard")} primaryTypographyProps={{ fontWeight: 700 }} />
            </ListItemButton>
          )}
        </List>

        <Divider sx={{ mx: 2 }} />

        {/* Mobile Country Selector */}
        <Box sx={{ px: 2, py: 2 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: "block" }}>
            {t("choose_country")}
          </Typography>
          <FormControl size="small" fullWidth>
            <Select
              value={selectedCountryCode}
              onChange={handleCountryChange}
              displayEmpty
              sx={{ borderRadius: 2, bgcolor: "grey.50" }}
              renderValue={(val) => {
                if (!val) return <Typography variant="body2" color="text.secondary">{t("choose_country")}</Typography>;
                return (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {selectedCountry?.image ? (
                      <Box
                        component="img"
                        src={resolveImageUrl(selectedCountry.image)}
                        alt=""
                        sx={{ width: 22, height: 16, objectFit: "cover", borderRadius: 0.5 }}
                      />
                    ) : (
                      <span>{selectedCountry?.flag || ""}</span>
                    )}
                    <Typography variant="body2" fontWeight={600}>{displayedCountryName}</Typography>
                  </Box>
                );
              }}
            >
              <MenuItem value="">
                <em>{t("choose_country")}</em>
              </MenuItem>
              {(countries || []).map((c) => (
                <MenuItem key={c.id} value={c.code || c.id}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {c.image ? (
                      <Box
                        component="img"
                        src={resolveImageUrl(c.image)}
                        alt=""
                        sx={{ width: 22, height: 16, objectFit: "cover", borderRadius: 0.5 }}
                      />
                    ) : (
                      <span>{c.flag || ""}</span>
                    )}
                    <span>{(i18n?.language || "en").startsWith("ar") ? c.name_ar : c.name_en}</span>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Divider sx={{ mx: 2 }} />

        {/* Mobile Footer Actions */}
        <Box sx={{ px: 2, py: 2, mt: "auto" }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={toggleLanguage}
            startIcon={<LanguageIcon />}
            sx={{
              mb: 1.5,
              borderRadius: 3,
              fontWeight: 700,
              textTransform: "none",
              borderWidth: 2,
              "&:hover": { borderWidth: 2 },
            }}
          >
            {i18n.language === "ar" ? "English" : "العربية"}
          </Button>

          {!isAuth ? (
            <Button
              fullWidth
              variant="contained"
              onClick={() => handleMobileNav("/login")}
              startIcon={<PersonOutlineIcon />}
              sx={{
                borderRadius: 3,
                fontWeight: 700,
                textTransform: "none",
                py: 1.2,
                background: "linear-gradient(135deg, #d5aa2f 0%, #e6c76a 100%)",
                "&:hover": {
                  background: "linear-gradient(135deg, #b08c26 0%, #d5aa2f 100%)",
                },
              }}
            >
              {t("login")}
            </Button>
          ) : (
            <Button
              fullWidth
              variant="outlined"
              color="error"
              onClick={() => { handleLogout(); setMobileOpen(false); }}
              startIcon={<ExitToAppIcon />}
              sx={{
                borderRadius: 3,
                fontWeight: 700,
                textTransform: "none",
                borderWidth: 2,
                "&:hover": { borderWidth: 2 },
              }}
            >
              {t("logout", { defaultValue: "تسجيل خروج" })}
            </Button>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default Navbar;
